import '../styles/GlobalDesign.css';
import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

const initialForm = {
  mac_address: '',
  user_id: '',
  xtream_account_id: '',
  device_name: 'MAG Box',
  expires_at: '',
  max_connections: 1
};

function dateTimeLocalPlusDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR');
}

function isExpired(value) {
  return value ? new Date(value) < new Date() : false;
}

function normalizeMacInput(value) {
  const clean = String(value || '').replace(/[^0-9a-f]/gi, '').toUpperCase().slice(0, 12);
  return clean.match(/.{1,2}/g)?.join(':') || '';
}

function normalizeSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function matchesSearch(values, query) {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return true;
  return values.some(value => normalizeSearch(value).includes(normalizedQuery));
}

function AdminMacActivation() {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [xtreamAccounts, setXtreamAccounts] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [xtreamSearch, setXtreamSearch] = useState('');
  const [portalUrl, setPortalUrl] = useState('http://51.254.135.114/stalker_portal/c/');
  const [form, setForm] = useState(() => ({ ...initialForm, expires_at: dateTimeLocalPlusDays(30) }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const authHeaders = (json = false) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    if (json) headers['Content-Type'] = 'application/json';
    return headers;
  };

  const loadData = async () => {
    setError('');
    try {
      const [accountsRes, optionsRes] = await Promise.all([
        fetch(`${API_URL}/mac`, { headers: authHeaders() }),
        fetch(`${API_URL}/mac/options`, { headers: authHeaders() })
      ]);

      if (accountsRes.status === 401 || accountsRes.status === 403) {
        window.location.href = '/admin/login';
        return;
      }
      if (!accountsRes.ok) throw new Error('Impossible de charger les comptes MAC');
      if (!optionsRes.ok) throw new Error('Impossible de charger les options MAC');

      const accountsData = await accountsRes.json();
      const optionsData = await optionsRes.json();
      setAccounts(accountsData);
      setUsers(optionsData.users || []);
      setXtreamAccounts(optionsData.xtreamAccounts || []);
      setPortalUrl(optionsData.portal_url || portalUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => (
    users.filter(user => matchesSearch([user.nom, user.email], userSearch))
  ), [users, userSearch]);

  const filteredXtreamAccounts = useMemo(() => {
    const query = normalizeSearch(xtreamSearch);
    const source = form.user_id && !query
      ? xtreamAccounts.filter(acc => String(acc.user_id) === String(form.user_id))
      : xtreamAccounts;

    return source.filter(acc => matchesSearch([
      acc.username,
      acc.nom,
      acc.email,
      acc.reseller_name,
      acc.is_trial === 1 ? 'test demo trial essai' : '',
      acc.is_enabled === 0 ? 'desactive suspendu disabled' : '',
      acc.is_expired === 1 ? 'expire expired' : ''
    ], query));
  }, [form.user_id, xtreamAccounts, xtreamSearch]);

  const selectedXtreamAccount = useMemo(() => (
    xtreamAccounts.find(acc => String(acc.id) === String(form.xtream_account_id))
  ), [form.xtream_account_id, xtreamAccounts]);

  const stats = useMemo(() => {
    const active = accounts.filter(acc => acc.is_enabled === 1 && !isExpired(acc.expires_at)).length;
    const expired = accounts.filter(acc => isExpired(acc.expires_at)).length;
    const disabled = accounts.filter(acc => acc.is_enabled !== 1).length;
    return { total: accounts.length, active, expired, disabled };
  }, [accounts]);

  const updateForm = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: field === 'mac_address' ? normalizeMacInput(value) : value,
      ...(field === 'user_id' && selectedXtreamAccount && String(selectedXtreamAccount.user_id) !== String(value)
        ? { xtream_account_id: '' }
        : {})
    }));
  };

  const updateXtreamAccount = (value) => {
    const account = xtreamAccounts.find(acc => String(acc.id) === String(value));
    setForm(prev => ({
      ...prev,
      xtream_account_id: value,
      ...(account?.user_id ? { user_id: String(account.user_id) } : {})
    }));
  };

  const formatXtreamOption = (account) => {
    const tags = [
      account.is_trial === 1 ? 'TEST' : '',
      account.is_enabled === 0 ? 'OFF' : '',
      account.is_expired === 1 ? 'EXPIRÉ' : '',
      account.reseller_name ? `Revendeur: ${account.reseller_name}` : ''
    ].filter(Boolean);

    return [
      account.username,
      tags.length ? `[${tags.join(' / ')}]` : '',
      account.nom || account.email || '',
      `expire ${formatDate(account.expires_at)}`
    ].filter(Boolean).join(' - ');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/mac`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          mac_address: form.mac_address,
          user_id: form.user_id,
          xtream_account_id: form.xtream_account_id || null,
          device_name: form.device_name,
          expires_at: new Date(form.expires_at).toISOString(),
          max_connections: Number(form.max_connections) || 1
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec création MAC');

      setForm({ ...initialForm, expires_at: dateTimeLocalPlusDays(30) });
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (account) => {
    try {
      const response = await fetch(`${API_URL}/mac/${account.id}/toggle`, {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec modification');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRenew = async (account, days) => {
    try {
      const response = await fetch(`${API_URL}/mac/${account.id}/renew`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ days })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec renouvellement');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSync = async (account) => {
    try {
      const response = await fetch(`${API_URL}/mac/${account.id}/sync-xtream`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec synchronisation');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Supprimer l'activation MAC ${account.mac_address} ?`)) return;
    try {
      const response = await fetch(`${API_URL}/mac/${account.id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Échec suppression');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyActivation = async (account) => {
    const text = [
      'Activation MAC Fluxivo',
      `Portal URL: ${account.portal_url || portalUrl}`,
      `MAC: ${account.mac_address}`,
      `Appareil: ${account.device_name || 'MAG Box'}`,
      `Expire: ${formatDate(account.expires_at)}`
    ].join('\n');

    await navigator.clipboard.writeText(text);
    setCopiedId(account.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <AdminLayout>
      <div className="admin-header">
        <div>
          <h1>Activation MAC</h1>
          <p style={styles.subtitle}>MAG, Formuler, Dreamlink et applications Stalker Portal</p>
        </div>
        <button className="btn-back" onClick={() => window.location.href = '/admin/home'}>Retour</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={styles.statsGrid}>
        <div style={styles.stat}><strong>{stats.total}</strong><span>Total</span></div>
        <div style={styles.stat}><strong>{stats.active}</strong><span>Actifs</span></div>
        <div style={styles.stat}><strong>{stats.expired}</strong><span>Expirés</span></div>
        <div style={styles.stat}><strong>{stats.disabled}</strong><span>Désactivés</span></div>
      </div>

      <div style={styles.portalBox}>
        <div>
          <span style={styles.label}>Portal URL à mettre dans la box</span>
          <strong style={styles.portalText}>{portalUrl}</strong>
        </div>
        <button style={styles.smallButton} onClick={() => navigator.clipboard.writeText(portalUrl)}>Copier</button>
      </div>

      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.formHeader}>
          <h2>Nouvelle activation</h2>
          <div style={styles.presets}>
            {[7, 30, 90, 365].map(days => (
              <button key={days} type="button" style={styles.presetButton} onClick={() => updateForm('expires_at', dateTimeLocalPlusDays(days))}>
                {days === 365 ? '1 an' : `${days}j`}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.formGrid}>
          <label style={styles.field}>
            <span>Adresse MAC</span>
            <input
              value={form.mac_address}
              onChange={(e) => updateForm('mac_address', e.target.value)}
              placeholder="00:1A:79:AA:BB:CC"
              required
            />
          </label>

          <label style={styles.field}>
            <span>Client</span>
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Filtrer par nom ou email"
              style={styles.searchInput}
            />
            <select value={form.user_id} onChange={(e) => updateForm('user_id', e.target.value)} required>
              <option value="">Choisir un client</option>
              {filteredUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.nom || user.email} - {user.email}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span>Compte Xtream lié</span>
            <input
              value={xtreamSearch}
              onChange={(e) => setXtreamSearch(e.target.value)}
              placeholder="Filtrer username, client, revendeur"
              style={styles.searchInput}
            />
            <select value={form.xtream_account_id} onChange={(e) => updateXtreamAccount(e.target.value)}>
              <option value="">Auto: créer un compte interne MAC</option>
              {filteredXtreamAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {formatXtreamOption(account)}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.field}>
            <span>Nom appareil</span>
            <input value={form.device_name} onChange={(e) => updateForm('device_name', e.target.value)} />
          </label>

          <label style={styles.field}>
            <span>Expiration</span>
            <input type="datetime-local" value={form.expires_at} onChange={(e) => updateForm('expires_at', e.target.value)} required />
          </label>

          <label style={styles.field}>
            <span>Connexions</span>
            <input type="number" min="1" max="10" value={form.max_connections} onChange={(e) => updateForm('max_connections', e.target.value)} />
          </label>
        </div>

        <button style={styles.primaryButton} disabled={saving}>
          {saving ? 'Création...' : 'Activer cette MAC'}
        </button>
      </form>

      <div style={styles.listHeader}>
        <h2>Comptes MAC</h2>
        <button style={styles.smallButton} onClick={loadData}>Rafraîchir</button>
      </div>

      {loading ? (
        <div style={styles.empty}>Chargement...</div>
      ) : accounts.length === 0 ? (
        <div style={styles.empty}>Aucune activation MAC pour le moment.</div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>MAC</th>
                <th>Client</th>
                <th>Appareil</th>
                <th>Xtream</th>
                <th>Expiration</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(account => {
                const expired = isExpired(account.expires_at);
                const active = account.is_enabled === 1 && !expired;
                return (
                  <tr key={account.id}>
                    <td style={styles.mono}>{account.mac_address}</td>
                    <td>
                      <strong>{account.nom || '-'}</strong>
                      <div style={styles.muted}>{account.email || ''}</div>
                    </td>
                    <td>{account.device_name || 'MAG Box'}</td>
                    <td style={styles.mono}>{account.xtream_username || 'auto'}</td>
                    <td>{formatDate(account.expires_at)}</td>
                    <td>
                      <span style={{ ...styles.badge, ...(active ? styles.badgeActive : expired ? styles.badgeExpired : styles.badgeDisabled) }}>
                        {active ? 'Actif' : expired ? 'Expiré' : 'Désactivé'}
                      </span>
                    </td>
                    <td>
                      <div style={styles.actions}>
                        <button style={styles.actionButton} onClick={() => copyActivation(account)}>
                          {copiedId === account.id ? 'Copié' : 'Copier'}
                        </button>
                        <button style={styles.actionButton} onClick={() => handleRenew(account, 30)}>+30j</button>
                        <button style={styles.actionButton} onClick={() => handleSync(account)}>Sync</button>
                        <button style={styles.actionButton} onClick={() => handleToggle(account)}>
                          {account.is_enabled === 1 ? 'Suspendre' : 'Activer'}
                        </button>
                        <button style={styles.dangerButton} onClick={() => handleDelete(account)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  subtitle: {
    color: '#94A3B8',
    marginTop: 4
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 16
  },
  stat: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  portalBox: {
    background: '#0F172A',
    border: '1px solid rgba(34, 197, 94, 0.35)',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap'
  },
  label: {
    display: 'block',
    color: '#94A3B8',
    fontSize: 13,
    marginBottom: 4
  },
  portalText: {
    color: '#86EFAC',
    wordBreak: 'break-all'
  },
  form: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 18,
    marginBottom: 20
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap'
  },
  presets: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  presetButton: {
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: '#1F2937',
    color: '#E5E7EB',
    borderRadius: 6,
    padding: '8px 10px',
    cursor: 'pointer'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 14
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: '#CBD5E1',
    fontSize: 13
  },
  searchInput: {
    marginBottom: 4
  },
  primaryButton: {
    marginTop: 16,
    background: '#16A34A',
    color: 'white',
    border: 0,
    borderRadius: 8,
    padding: '12px 16px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  smallButton: {
    background: '#1F2937',
    color: '#E5E7EB',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 6,
    padding: '9px 12px',
    cursor: 'pointer'
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10
  },
  empty: {
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8,
    padding: 24,
    color: '#94A3B8',
    textAlign: 'center'
  },
  tableWrap: {
    overflowX: 'auto',
    background: '#111827',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 8
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 980
  },
  mono: {
    fontFamily: 'monospace',
    color: '#A7F3D0'
  },
  muted: {
    color: '#94A3B8',
    fontSize: 12,
    marginTop: 3
  },
  badge: {
    display: 'inline-flex',
    padding: '5px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700
  },
  badgeActive: {
    color: '#86EFAC',
    background: 'rgba(22, 163, 74, 0.14)'
  },
  badgeExpired: {
    color: '#FCA5A5',
    background: 'rgba(239, 68, 68, 0.14)'
  },
  badgeDisabled: {
    color: '#CBD5E1',
    background: 'rgba(148, 163, 184, 0.14)'
  },
  actions: {
    display: 'flex',
    gap: 7,
    flexWrap: 'wrap'
  },
  actionButton: {
    background: '#1F2937',
    color: '#E5E7EB',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 6,
    padding: '7px 9px',
    cursor: 'pointer'
  },
  dangerButton: {
    background: 'rgba(239, 68, 68, 0.12)',
    color: '#FCA5A5',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 6,
    padding: '7px 9px',
    cursor: 'pointer'
  }
};

export default AdminMacActivation;
